/** Where a tax row's bookkeeping stands, as the tax account page reports it (FIN-769 S1). */
export interface TaxRowBookkeeping {
  suggestionId: string | null;
  holdReason: string | null;
  /** When the hold began (ISO); null when the row is not held (FIN-1617). */
  heldAt?: string | null;
  /** A consultant's resolution of a held deposit or payout (FIN-1617). */
  resolution?: string | null;
}

export type TaxBookkeepingStatus =
  | { kind: 'review'; href: string }
  | { kind: 'held_sie_overlap' }
  | { kind: 'held_payment_bypassed_1630' }
  /** FIN-1167: the owner's private tax, never booked. */
  | { kind: 'outside_books' }
  | { kind: 'closed_fiscal_year' }
  | { kind: 'awaiting_bank_match' }
  /** FIN-1617: a consultant marked it already booked; there is no entry of its own. */
  | { kind: 'resolved_already_booked' }
  /** FIN-1617: a consultant sent it to Review; the Review item is on its way. */
  | { kind: 'resolved_paid_from_outside' }
  | { kind: 'none' };

/**
 * One state per row (specs/tax-account-bookkeeping/screens.md S1). The link is always the
 * suggestion, never its journal entry (learnings/031). Holds without a badge of their own show
 * nothing yet: their named alert is the signal (plan Q2).
 */
export function taxBookkeepingStatus(bookkeeping: TaxRowBookkeeping | undefined): TaxBookkeepingStatus {
  // FIN-976 EC8: the charge's suggestion was ended when it was held, so the hold, not that
  // ended suggestion, is where its bookkeeping stands.
  if (bookkeeping?.holdReason === 'payment_bypassed_1630') {
    return { kind: 'held_payment_bypassed_1630' };
  }
  // FIN-1167: a private row never has a suggestion; one rejected as private ends up here too.
  if (bookkeeping?.holdReason === 'outside_books') {
    return { kind: 'outside_books' };
  }
  if (bookkeeping?.suggestionId) {
    return { kind: 'review', href: `/review?suggestion=${encodeURIComponent(bookkeeping.suggestionId)}` };
  }
  if (bookkeeping?.resolution === 'already_booked') return { kind: 'resolved_already_booked' };
  if (bookkeeping?.resolution === 'paid_from_outside') return { kind: 'resolved_paid_from_outside' };
  switch (bookkeeping?.holdReason) {
    case 'sie_overlap':
      return { kind: 'held_sie_overlap' };
    case 'closed_fiscal_year':
      return { kind: 'closed_fiscal_year' };
    case 'awaiting_bank_match':
      return { kind: 'awaiting_bank_match' };
    default:
      return { kind: 'none' };
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How long a row has been held, in whole days at `now` (FIN-1617). Null when it is not held or the
 * time is unreadable: an unknown age is never shown as zero.
 */
export function heldForDays(heldAt: string | null | undefined, now: Date): number | null {
  if (!heldAt) return null;
  const since = new Date(heldAt).getTime();
  if (Number.isNaN(since)) return null;
  return Math.max(0, Math.floor((now.getTime() - since) / MS_PER_DAY));
}
